/**
 * Hook for payment error handling
 * Contains mapping of error codes to user-friendly messages
 */

const DEFAULT_PAYMENT_ERROR_MESSAGE = 'Payment processing error. Please try again or use another payment method.';

const errorMessages: Record<string, string> = {
  // Card declined errors
  card_declined: 'Your card was declined. Contact your bank or use another card.',
  generic_decline: 'Your card was declined. Contact your bank or use another card.',
  do_not_honor: 'Your card was declined by the bank. Contact your bank or use another card.',
  insufficient_funds: 'Your card has insufficient funds. Please use another card or add funds to your account.',
  card_velocity_exceeded: 'You have exceeded the maximum number of attempts. Please try again later or use another card.',
  lost_card: 'Your card was declined because it has been reported as lost. Please use another card.',
  stolen_card: 'Your card was declined because it has been reported as stolen. Please use another card.',
  expired_card: 'Your card has expired. Please use another card.',
  incorrect_cvc: 'Your card\'s security code is incorrect. Please check your CVC code.',
  incorrect_number: 'Your card number is incorrect. Please check your card number.',
  invalid_cvc: 'Your card\'s security code is invalid. Please check your CVC code.',
  invalid_expiry_month: 'Your card\'s expiration month is invalid. Please check your card details.',
  invalid_expiry_year: 'Your card\'s expiration year is invalid. Please check your card details.',
  invalid_number: 'Your card number is invalid. Please check your card number.',
  processing_error: 'An error occurred while processing your card. Please try again.',
  reenter_transaction: 'Please try your transaction again.',
  restricted_card: 'Your card was declined. Please contact your bank or use another card.',
  security_violation: 'Your card was declined due to a security violation. Please contact your bank.',
  service_not_allowed: 'Your card was declined. Please contact your bank or use another card.',
  stop_payment_order: 'Your card was declined. Please contact your bank or use another card.',
  testmode_decline: 'Your card was declined in test mode. Please use a test card number.',
  transaction_not_allowed: 'Your card was declined. Please contact your bank or use another card.',
  try_again_later: 'Your card was declined. Please try again later or use another card.',
  withdrawal_count_limit_exceeded: 'You have exceeded the maximum number of attempts. Please try again later.',

  // Authentication errors
  authentication_required: 'Additional authentication is required. Please complete the verification process.',
  setup_intent_authentication_failure: 'Authentication failed. Please try again.',
  setup_intent_unexpected_state: 'Payment method setup is already in progress. Please wait a moment and try again.',
  requires_payment_method: 'Please add a payment method to continue.',

  // Apple Pay errors
  apple_pay_canceled: 'Payment was cancelled by user.',
  apple_pay_failed: 'Apple Pay error. Please try again or use another payment method.',

  // Network and system errors
  network_error: 'Network error. Please check your internet connection and try again.',
  timeout: 'Request timeout. Please try again.',
  api_connection_error: 'Unable to connect to payment processor. Please try again.',
  api_error: 'Payment processor error. Please try again.',
  rate_limit_error: 'Too many requests. Please try again later.',

  // Invalid request errors
  invalid_request_error: 'Invalid payment request. Please check your information and try again.',
  card_error: 'There was an error with your card. Please check your card details and try again.',
  idempotency_key_in_use: 'This payment is already being processed. Please wait a moment.',

  // Bank account errors (ACH)
  account_closed: 'The bank account is closed. Please use another account.',
  account_frozen: 'The bank account is frozen. Please use another account.',
  bank_account_restricted: 'The bank account is restricted. Please use another account.',
  bank_ownership_changed: 'Bank account ownership has changed. Please use another account.',
  debit_not_authorized: 'Debits are not authorized for this account. Please use another account.',
  bank_insufficient_funds: 'Insufficient funds in the bank account. Please use another account.',
  invalid_account_number: 'Invalid bank account number. Please check your account details.',
  invalid_currency: 'Invalid currency for this bank account. Please use another account.',
  no_account: 'No account found with this information. Please check your account details.',
  unsupported_account: 'This type of account is not supported. Please use another account.',

  // 3D Secure errors
  three_d_secure_authentication_required: 'Additional authentication is required. Please complete the verification process.',
  three_d_secure_authentication_failed: 'Authentication failed. Please try again.',

  // Webhook errors
  webhook_error: 'Webhook processing error. Please try again.',

  // Rate limiting
  rate_limit_exceeded: 'Too many requests. Please try again later.',
};

function getStringField(error: object, field: string): string | null {
  const value = (error as Record<string, unknown>)[field];
  return typeof value === 'string' ? value : null;
}

function getErrorCode(error: unknown): string | null {
  if (!error || typeof error !== 'object') {
    return null;
  }

  return getStringField(error, 'code')
    || getStringField(error, 'type')
    || getStringField(error, 'decline_code');
}

function getRawErrorMessage(error: unknown): string | null {
  if (typeof error === 'string') {
    return error;
  }

  if (error instanceof Error) {
    return error.message;
  }

  if (error && typeof error === 'object') {
    return getStringField(error, 'message');
  }

  return null;
}

function getMessageBasedError(error: unknown): string | null {
  const rawMessage = getRawErrorMessage(error);
  if (!rawMessage) {
    return null;
  }

  const message = rawMessage.toLowerCase();

  if (message.includes('insufficient_funds')) {
    return errorMessages.insufficient_funds;
  }

  if (message.includes('do_not_honor')) {
    return errorMessages.do_not_honor;
  }

  if (message.includes('card_velocity_exceeded')) {
    return errorMessages.card_velocity_exceeded;
  }

  if (message.includes('apple pay not available') || message.includes('apple pay is not available')) {
    return 'Apple Pay is not available. Please use Safari on iOS or macOS and ensure Apple Pay is set up.';
  }

  if (message.includes('apple pay is still initializing')) {
    return 'Apple Pay is still initializing. Please wait a moment.';
  }

  if (message.includes('apple pay is not ready')) {
    return 'Apple Pay is not ready. Please refresh the page.';
  }

  if (message.includes('stripe not configured')) {
    return 'Payment system configuration error. Please refresh the page.';
  }

  if (message.includes('no trial plan available')) {
    return 'Trial plan is not available. Please refresh the page.';
  }

  if (message.includes('init failed') || message.includes('no client token')) {
    return 'Payment initialization error. Check your internet connection and try again.';
  }

  if (message.includes('payment confirmation failed')) {
    return 'Payment confirmation error. Check your card details and try again.';
  }

  if (message.includes('3d secure')) {
    return '3D Secure authentication error. Please contact your bank for assistance.';
  }

  if (message.includes('subscription confirm')) {
    return 'Subscription confirmation error. Payment may have been processed, please check your subscription status.';
  }

  if (message.includes('apple pay')) {
    return errorMessages.apple_pay_failed;
  }

  if (/[\u0400-\u04FF]/.test(rawMessage)) {
    return DEFAULT_PAYMENT_ERROR_MESSAGE;
  }

  return null;
}

/**
 * Get user-friendly error message by error code
 */
export function getErrorMessage(error: unknown): string {
  // Log error for debugging
  console.error('Payment error:', error);
  console.error('Error type:', typeof error);
  console.error('Error constructor:', error?.constructor?.name);

  if (error && typeof error === 'object') {
    console.error('Error keys:', Object.keys(error));
    console.error('Error properties:', {
      code: 'code' in error ? error.code : 'not found',
      type: 'type' in error ? error.type : 'not found',
      message: 'message' in error ? error.message : 'not found',
      decline_code: 'decline_code' in error ? error.decline_code : 'not found',
      status: 'status' in error ? error.status : 'not found',
      statusCode: 'statusCode' in error ? error.statusCode : 'not found',
      data: 'data' in error ? error.data : 'not found',
    });
  }

  // If it's an object with error code
  if (error && typeof error === 'object') {
    // Check various possible error code fields
    const errorCode = getErrorCode(error);

    console.error('Extracted error code:', errorCode);

    if (errorCode && errorMessages[errorCode]) {
      console.error('Found mapped error message for code:', errorCode);
      return errorMessages[errorCode];
    }
  }

  const messageBasedError = getMessageBasedError(error);
  if (messageBasedError) {
    return messageBasedError;
  }

  // Return default error message
  return DEFAULT_PAYMENT_ERROR_MESSAGE;
}

/**
 * Check if error is critical (requires re-authentication or user action)
 */
export function isCriticalError(error: unknown): boolean {
  if (error && typeof error === 'object') {
    const errorCode
      = ('code' in error && typeof error.code === 'string' ? error.code : null)
        || ('type' in error && typeof error.type === 'string' ? error.type : null)
        || ('decline_code' in error && typeof error.decline_code === 'string' ? error.decline_code : null);

    if (errorCode) {
      return [
        // Authentication errors
        'authentication_required',
        'setup_intent_authentication_failure',
        'setup_intent_unexpected_state',
        'three_d_secure_authentication_required',
        'three_d_secure_authentication_failed',

        // Card declined errors that require user action
        'card_declined',
        'generic_decline',
        'do_not_honor',
        'insufficient_funds',
        'expired_card',
        'incorrect_cvc',
        'incorrect_number',
        'invalid_cvc',
        'invalid_expiry_month',
        'invalid_expiry_year',
        'invalid_number',
        'lost_card',
        'stolen_card',
        'restricted_card',
        'security_violation',
        'service_not_allowed',
        'stop_payment_order',
        'transaction_not_allowed',

        // Bank account errors
        'account_closed',
        'account_frozen',
        'bank_account_restricted',
        'bank_ownership_changed',
        'debit_not_authorized',
        'bank_insufficient_funds',
        'invalid_account_number',
        'invalid_currency',
        'no_account',
        'unsupported_account',

        // Payment method errors
        'requires_payment_method',
        'card_error',
      ].includes(errorCode);
    }
  }

  return false;
}

export function usePaymentErrors() {
  return {
    errorMessages,
    getErrorMessage,
    isCriticalError,
  };
}
